import { useMemo } from 'react'
import { useAppSelector } from '@/app/hooks'
import { selectUser } from '@/features/auth/authSlice'
import type { UserRole } from '@/types/auth'
import {
  LIBERTY_ROLES,
  COMPANY_ROLES,
  PROPERTY_ROLES,
  CHARITY_ROLES,
  USER_ACCESS,
} from '@/constants/roles'
import { normalizeRolePolicies } from '@/lib/auth/normalizeRolePolicies'

/**
 * Hook to access the current user's role information and role-based utilities.
 * Mirrors the Angular app's AuthService role methods and user-access.ts logic.
 */
export function useUserRole() {
  const user = useAppSelector(selectUser)

  return useMemo(() => {
    const currentRole: UserRole | undefined = user?.current_role
    const roleName = currentRole?.role_name || ''

    const isLibertyRole = (LIBERTY_ROLES as readonly string[]).includes(roleName)
    const isCompanyRole = (COMPANY_ROLES as readonly string[]).includes(roleName)
    const isPropertyRole = (PROPERTY_ROLES as readonly string[]).includes(roleName)
    const isCharityRole = (CHARITY_ROLES as readonly string[]).includes(roleName)

    const accessibleMenu = USER_ACCESS[roleName] || {}

    /**
     * Check if the current role has the given policy keys.
     * Mirrors Angular's AuthService.hasPolicy():
     *  - ROLE_ADMIN always returns true
     *  - Otherwise checks current_role.policy array
     */
    const hasPolicy = (policyKeys: string[]): boolean => {
      if (roleName === 'ROLE_ADMIN') return true
      if (!policyKeys || policyKeys.length === 0) return false

      const policies = normalizeRolePolicies(currentRole?.policy)
      if (policies.length === 0) return false

      return policyKeys.some((key) => policies.includes(key))
    }

    /**
     * Check if the current role's accessible menu includes a specific
     * feature with Write permission.
     * Mirrors Angular: accessibleMenu.invite && accessibleMenu.invite.includes('Write')
     */
    const hasWriteAccess = (feature: string): boolean => {
      return !!accessibleMenu[feature] && accessibleMenu[feature].includes('Write')
    }

    /**
     * Check if the current role has any access to a feature.
     */
    const hasAccess = (feature: string): boolean => {
      return !!accessibleMenu[feature]
    }

    return {
      user,
      currentRole,
      roleName,

      isLibertyRole,
      isCompanyRole,
      isPropertyRole,
      isCharityRole,

      accessibleMenu,

      hasPolicy,
      hasWriteAccess,
      hasAccess,

      isAdmin: roleName === 'ROLE_ADMIN',
      isCustomerService: roleName === 'ROLE_CUSTOMER_SERVICE',
      isCustomerServiceManager: roleName === 'ROLE_CUSTOMER_SERVICE_MANAGER',

      propertyId: currentRole?.property_id ?? null,
      companyId: currentRole?.company_id ?? null,
      orgId: currentRole?.org_id ?? null,
      propertyName: currentRole?.property_name ?? null,
      companyName: currentRole?.company_name ?? null,
      orgName: currentRole?.org_name ?? null,
    }
  }, [user])
}
